import { useEffect, useRef, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { ArrowUp, ConciergeBell } from "lucide-react";
import { toast } from "sonner";
import { ChatBubble, TypingIndicator } from "@/components/ChatBubble";
import { DockBar } from "@/components/layout/Page";
import { keys, useChatMessages } from "@/hooks/queries";
import { streamChat } from "@/lib/chatStream";

const PROMPTS = [
  "Book me an airport transfer tomorrow",
  "Recommend a fine dining spot in Buckhead",
  "I need a chauffeur for 4 hours Friday",
];

function EmptyState({ onPick }) {
  return (
    <div className="rise py-10 text-center" data-testid="concierge-empty">
      <p className="mx-auto max-w-sm font-display text-2xl leading-snug text-ivory">How may I arrange your evening?</p>
      <p className="mt-2 text-sm text-mist">Rides, reservations, recommendations — simply ask.</p>
      <div className="mt-6 flex flex-col items-center gap-2 sm:flex-row sm:flex-wrap sm:justify-center">
        {PROMPTS.map((p, i) => (
          <button key={p} type="button" className="chip" onClick={() => onPick(p)} data-testid={`concierge-prompt-chip-${i}`}>
            {p}
          </button>
        ))}
      </div>
    </div>
  );
}

function Composer({ value, onChange, onSend, disabled }) {
  const submit = (e) => {
    e.preventDefault();
    onSend(value);
  };
  return (
    <form onSubmit={submit} className="glass flex items-end gap-2 rounded-3xl p-2 pl-5 shadow-[0_18px_40px_rgba(0,0,0,0.6)]" data-testid="concierge-composer">
      <textarea
        rows={1}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === "Enter" && !e.shiftKey) submit(e);
        }}
        maxLength={2000}
        placeholder="Message your concierge…"
        className="max-h-32 flex-1 resize-none bg-transparent py-2.5 text-sm text-ivory placeholder:text-faint focus:outline-none"
        data-testid="concierge-input"
      />
      <button
        type="submit"
        disabled={disabled || !value.trim()}
        className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-gold text-ink transition-[transform,opacity] duration-200 hover:bg-gold-soft active:scale-95 disabled:opacity-30"
        data-testid="concierge-send-btn"
        aria-label="Send"
      >
        <ArrowUp className="h-5 w-5" />
      </button>
    </form>
  );
}

export default function Concierge() {
  const qc = useQueryClient();
  const { data: messages = [], isLoading, isError, refetch } = useChatMessages();
  const [live, setLive] = useState(null);
  const [text, setText] = useState("");
  const endRef = useRef(null);

  useEffect(() => {
    endRef.current?.scrollIntoView({ behavior: "smooth", block: "end" });
  }, [messages.length, live?.reply, live?.user]);

  const send = async (content) => {
    const body = content.trim();
    if (!body || live) return;
    setText("");
    setLive({ user: { id: "optimistic", role: "user", content: body }, reply: "" });
    try {
      const saved = await streamChat(body, (chunk) => setLive((l) => l && { ...l, reply: l.reply + chunk }));
      qc.setQueryData(keys.chat, (old = []) => [...old, saved.user, saved.reply]);
    } catch (e) {
      toast.error(e.message);
      qc.invalidateQueries({ queryKey: keys.chat });
    } finally {
      setLive(null);
    }
  };

  return (
    <div className="pb-28" data-testid="concierge-page">
      <div className="rise mb-6 flex items-center gap-4">
        <span className="flex h-14 w-14 items-center justify-center rounded-full border border-gold/40 bg-gold/10 text-gold">
          <ConciergeBell className="h-6 w-6" strokeWidth={1.5} />
        </span>
        <div>
          <h1 className="text-3xl tracking-tight text-ivory sm:text-4xl">Your Concierge</h1>
          <p className="mt-1 flex items-center gap-2 text-xs text-mist">
            <span className="h-1.5 w-1.5 rounded-full bg-confirmed" /> At your service, day and night
          </p>
        </div>
      </div>

      <div className="space-y-4" data-testid="concierge-messages">
        {isLoading && <div className="h-24 animate-pulse rounded-3xl bg-surface" />}
        {isError && (
          <p className="text-sm text-mist" data-testid="concierge-error">
            We couldn't load your conversation. <button type="button" className="text-gold underline" onClick={() => refetch()}>Retry</button>
          </p>
        )}
        {!isLoading && !isError && messages.length === 0 && !live && <EmptyState onPick={send} />}
        {messages.map((m) => <ChatBubble key={m.id} message={m} testId={`chat-message-${m.role}`} />)}
        {live && <ChatBubble message={live.user} testId="chat-message-optimistic" />}
        {live && (live.reply ? <ChatBubble message={{ role: "assistant", content: live.reply }} testId="chat-message-streaming" /> : <TypingIndicator />)}
        <div ref={endRef} />
      </div>

      <DockBar testId="concierge-dock">
        <Composer value={text} onChange={setText} onSend={send} disabled={Boolean(live)} />
      </DockBar>
    </div>
  );
}
